import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axiosInstance, {
  checkTokenValidity,
  CheckUserExpiry,
  getUserDetails,
  isCompanyManagerUser,
  isFisherOwnerUser,
  isSuperUserSession,
} from "../../components/content/axiosConfig";
import Select from "react-select";
import Swal from "sweetalert2";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import CustomModal from "../../components/custom/customModal";
import workflowSelectStyles from "../../components/content/workflowSelectStyles";
import { resolveStaffPondRole } from "../../utils/teamRoles";
import { resolveScopedFisherID } from "../../utils/pondListForUser";
import {
  formatDisplayDate as formatDateForMobile,
  formatDisplayDateTime as formatDateTime,
} from "../../utils/formatDisplayDate";

const cardRadius = { borderRadius: "14px" };

const pondAssignSelectStyles = {
  ...workflowSelectStyles,
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  menuList: (base) => ({
    ...base,
    maxHeight: 420,
    paddingTop: 4,
    paddingBottom: 4,
  }),
  menu: (base) => ({
    ...workflowSelectStyles.menu(base),
    marginTop: 4,
    borderRadius: "12px",
    overflow: "hidden",
  }),
  control: (base, state) => ({
    ...workflowSelectStyles.control(base, state),
    minHeight: "40px",
    borderRadius: "10px",
    fontSize: "14px",
  }),
  option: (base, state) => ({
    ...workflowSelectStyles.option(base, state),
    fontSize: "14px",
    padding: "8px 12px",
  }),
};

const ScientistPond = () => {
  const [ponds, setPonds] = useState([]);
  const [AllPonds, setAllPonds] = useState([]);
  const [staffRecord, setStaffRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [modalSaving, setModalSaving] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { id } = useParams();
  const userDetails = getUserDetails();
  const fisherID = resolveScopedFisherID(userDetails);
  const isCompanyManager = isCompanyManagerUser(userDetails);
  const canManageAssignments =
    isFisherOwnerUser(userDetails) || isCompanyManager || isSuperUserSession(userDetails);
  const teamListPath = "/staff";
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedPond, setSelectedPond] = useState(null);

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedPond(null);
  };

  const openAddModal = () => {
    setSelectedPond(null);
    setModalIsOpen(true);
  };

  const handlePondChange = (selectedOption) => {
    setSelectedPond(selectedOption);
  };

  const refreshPondsList = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`api/Pond/GetPondsByStaff/${id}`, {
        params: { fisherID: fisherID },
      });
      setPonds(response.data);
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to refresh ponds";
      showErrorToast(errorMessage);
      console.error("Error refreshing ponds:", error);
    }
  }, [id, fisherID]);

  const refreshAllPondsOptions = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`api/Pond/GetAllPond/${fisherID}`, {
        params: { search: "" },
      });
      const dropdownPonds =
        response.data?.map((pond) => ({
          label: pond.Name,
          value: pond.id,
        })) || [];
      setAllPonds(dropdownPonds);
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to load pond list";
      showErrorToast(errorMessage);
      console.error("Error fetching all ponds:", error);
    }
  }, [fisherID]);

  const loadPageData = useCallback(async () => {
    setIsLoading(true);
    try {
      checkTokenValidity();
      await CheckUserExpiry();
      const [staffRes, pondsRes, allRes] = await Promise.all([
        axiosInstance.get("api/User/GetStaff", { params: { id } }),
        axiosInstance.get(`api/Pond/GetPondsByStaff/${id}`, {
          params: { fisherID: fisherID },
        }),
        axiosInstance.get(`api/Pond/GetAllPond/${fisherID}`, {
          params: { search: "" },
        }),
      ]);
      setStaffRecord(staffRes.data);
      setPonds(pondsRes.data);
      const dropdownPonds =
        allRes.data?.map((pond) => ({
          label: pond.Name,
          value: pond.id,
        })) || [];
      setAllPonds(dropdownPonds);
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to load pond access";
      showErrorToast(errorMessage);
      console.error("Error loading page:", error);
    } finally {
      setIsLoading(false);
    }
  }, [id, fisherID]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (fisherID == null || fisherID === "") {
      setIsLoading(false);
      return;
    }
    loadPageData();
  }, [loadPageData, fisherID]);

  const unassignPond = async (pondID) => {
    const result = await Swal.fire({
      title: "Unassign pond?",
      text: "This member will lose access to this pond until you assign it again.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, unassign",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`api/ScientistPonds/Delete`, {
          params: {
            pondID,
            id,
            fisherID,
          },
        });
        setPonds((prev) => prev.filter((pond) => pond.id !== pondID));
        showSuccessToast("Pond unassigned");
      } catch (error) {
        const errorMessage = error.response?.data?.message || "Failed to unassign pond";
        showErrorToast(errorMessage);
        console.error("Error unassigning pond:", error);
      }
    }
  };

  const handleSubmit = async () => {
    if (!selectedPond?.value) {
      showErrorToast("Select a pond to assign.");
      return;
    }
    const pondRole = resolveStaffPondRole(staffRecord);

    setModalSaving(true);
    try {
      await axiosInstance.post(`/api/ScientistPonds/Add`, {
        FisherID: fisherID,
        StaffID: id,
        PondID: selectedPond.value,
        PondRole: pondRole,
      });
      showSuccessToast("Pond assigned");
      closeModal();
      await refreshPondsList();
      await refreshAllPondsOptions();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to assign pond";
      showErrorToast(errorMessage);
      console.error("Error assigning pond:", error);
    } finally {
      setModalSaving(false);
    }
  };

  if (isLoading) {
    return <Preloader />;
  }

  return (
    <div className="donezo-page">
      <div className="container-fluid px-3 px-md-4 py-3 py-md-4">
        <nav aria-label="breadcrumb" className="mb-2">
          <ol className="breadcrumb mb-0 small">
            <li className="breadcrumb-item">
              <Link to="/" className="text-decoration-none text-muted">
                Home
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={teamListPath} className="text-decoration-none text-muted">
                Team
              </Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Pond access
            </li>
          </ol>
        </nav>

        <div className="d-flex flex-column flex-md-row align-items-start justify-content-between gap-3 mb-4">
          <div>
            <h1 className="h3 fw-semibold text-dark mb-1">Pond access</h1>
            <p className="text-muted small mb-0">
              Manage which ponds this member can access. Set their role on the{" "}
              <Link to={`/UpdateStaff/${id}`}>General</Link> tab.
            </p>
          </div>
          {canManageAssignments && (
            <button
              type="button"
              className="btn btn-primary rounded-pill px-4 shadow-sm align-self-stretch align-self-md-start"
              onClick={openAddModal}
            >
              <i className="fa fa-plus me-2" aria-hidden />
              {windowWidth > 576 ? "Assign pond" : "Add"}
            </button>
          )}
        </div>

        <div className="card border-0 shadow-sm mb-4" style={cardRadius}>
          <div className="card-body p-3 p-md-4">
            <div className="d-flex flex-wrap gap-2">
              <Link
                to={"/UpdateStaff/" + id}
                className="badge rounded-pill px-3 py-2 bg-light text-secondary text-decoration-none border"
              >
                <i className="fa fa-user me-1 opacity-75" aria-hidden />
                General
              </Link>
              <Link
                to={"/changePassword/" + id}
                className="badge rounded-pill px-3 py-2 bg-light text-secondary text-decoration-none border"
              >
                <i className="fa fa-key me-1 opacity-75" aria-hidden />
                Password
              </Link>
              <span className="badge rounded-pill px-3 py-2 bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25">
                <i className="fa fa-water me-1 opacity-75" aria-hidden />
                Ponds
              </span>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm" style={cardRadius}>
          <div className="card-body p-0 p-md-1">
            <div className="d-none d-md-block table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th className="small text-muted text-uppercase fw-semibold border-0 ps-4" style={{ letterSpacing: "0.04em" }}>
                      Name
                    </th>
                    <th className="small text-muted text-uppercase fw-semibold border-0" style={{ letterSpacing: "0.04em" }}>
                      Location
                    </th>
                    <th className="small text-muted text-uppercase fw-semibold border-0" style={{ letterSpacing: "0.04em" }}>
                      Type
                    </th>
                    <th className="small text-muted text-uppercase fw-semibold border-0" style={{ letterSpacing: "0.04em" }}>
                      Depth
                    </th>
                    <th className="small text-muted text-uppercase fw-semibold border-0" style={{ letterSpacing: "0.04em" }}>
                      Created
                    </th>
                    <th className="small text-muted text-uppercase fw-semibold border-0" style={{ letterSpacing: "0.04em" }}>
                      Updated
                    </th>
                    <th className="small text-muted text-uppercase fw-semibold border-0 pe-4 text-end" style={{ letterSpacing: "0.04em" }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ponds.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="p-0">
                        <div
                          className="w-100 text-center py-5 px-4 rounded-3"
                          style={{ backgroundColor: "rgba(var(--bs-primary-rgb), 0.08)" }}
                        >
                          <div
                            className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3 text-white"
                            style={{ width: 56, height: 56, background: "linear-gradient(135deg, var(--bs-primary) 0%, #007d7d 100%)" }}
                          >
                            <i className="fa fa-water fs-4" aria-hidden />
                          </div>
                          <p className="fw-semibold text-dark mb-1">No ponds assigned yet</p>
                          <p className="small text-muted mb-3">
                            Select a pond to grant this member access.
                          </p>
                          {canManageAssignments && (
                            <button type="button" className="btn btn-primary rounded-pill px-4" onClick={openAddModal}>
                              <i className="fa fa-plus me-2" aria-hidden />
                              Assign first pond
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    ponds.map((pond) => (
                      <tr key={pond.id}>
                        <td className="ps-4 fw-medium text-dark">{pond.Name}</td>
                        <td className="text-secondary">{pond.Location || "—"}</td>
                        <td className="text-secondary">{pond.Type || "—"}</td>
                        <td className="text-secondary">{pond.Depth ?? "—"}</td>
                        <td className="text-secondary small">{formatDateTime(pond.createdAt)}</td>
                        <td className="text-secondary small">{formatDateTime(pond.updatedAt)}</td>
                        <td className="text-end pe-4">
                          {canManageAssignments && (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              title="Unassign pond"
                              aria-label="Unassign pond"
                              onClick={() => unassignPond(pond.id)}
                            >
                              <i className="fa fa-trash" aria-hidden />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-md-none p-3">
              {ponds.length === 0 ? (
                <div
                  className="w-100 text-center rounded-3 py-4 px-3"
                  style={{ backgroundColor: "rgba(var(--bs-primary-rgb), 0.08)" }}
                >
                  <p className="fw-semibold text-dark mb-1">No ponds assigned</p>
                  <p className="small text-muted mb-3">Assign ponds for this member.</p>
                  {canManageAssignments && (
                    <button type="button" className="btn btn-primary rounded-pill px-4" onClick={openAddModal}>
                      Assign pond
                    </button>
                  )}
                </div>
              ) : (
                ponds.map((pond) => (
                  <div key={pond.id} className="card border shadow-sm mb-3" style={{ borderRadius: "12px" }}>
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <h2 className="h6 fw-semibold text-dark mb-0">{pond.Name}</h2>
                        {canManageAssignments && (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger flex-shrink-0"
                            aria-label="Unassign pond"
                            title="Unassign pond"
                            onClick={() => unassignPond(pond.id)}
                          >
                            <i className="fa fa-trash" aria-hidden />
                          </button>
                        )}
                      </div>
                      <div className="row g-2 small mt-2">
                        <div className="col-6">
                          <span className="text-muted">Location</span>
                          <div className="text-dark">{pond.Location || "—"}</div>
                        </div>
                        <div className="col-6">
                          <span className="text-muted">Type</span>
                          <div className="text-dark">{pond.Type || "—"}</div>
                        </div>
                        <div className="col-6">
                          <span className="text-muted">Depth</span>
                          <div className="text-dark">{pond.Depth ?? "—"}</div>
                        </div>
                        <div className="col-6">
                          <span className="text-muted">Updated</span>
                          <div className="text-dark">{formatDateForMobile(pond.updatedAt)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <CustomModal
        isOpen={modalIsOpen}
        onClose={closeModal}
        title="Assign pond"
        showButton
        variant="modern"
        submitLabel="Assign pond"
        submitDisabled={modalSaving}
        modalSize="modal-md"
        minHeight="200px"
        maxHeight="90vh"
        onSubmit={handleSubmit}
      >
        <p className="small text-muted mb-4">
          Select a pond to grant this member access. Their role is set on the General tab.
        </p>
        <div className="mb-2">
          <label className="form-label small fw-medium text-secondary">Pond</label>
          <Select
            onChange={handlePondChange}
            options={AllPonds}
            value={selectedPond}
            styles={pondAssignSelectStyles}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
            isMulti={false}
            placeholder="Search or select a pond…"
            isClearable
            classNamePrefix="pond-assign"
          />
        </div>
      </CustomModal>
    </div>
  );
};

export default ScientistPond;
